import React, { useMemo, useState } from 'react'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../../../components/ui/accordion'
import { Button } from '../../../../components/ui/button'
import { Link } from 'react-router-dom'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import axios from '../../../../lib/axios'
import { useAuth } from '../../../../context/AuthProvider'
import { useBoundStore } from '../../../../zustand'
import { Trans, useLingui } from '@lingui/react/macro'
import { getCustomSettings } from '../../../../config/custom-settings'
import { format, parse, set as setTime } from 'date-fns'

type Props = {
  intervention: Intervention
}

type InterventionHistoryResponse = {
  page: number
  totalrecords: number
  totalpages: number
  interventions: Intervention[]
}

type FutureVisit = {
  name: string
  date: string
  starttime: string
  endtime: string
}

type FutureResponse = {
  interventions: FutureVisit[]
}

const toDateSpan = (v: FutureVisit) => {
  try {
    const base = parse(v.date, 'yyyyMMdd', new Date())
    const h1 = Number(v.starttime?.slice(0, 2) ?? 0)
    const m1 = Number(v.starttime?.slice(2, 4) ?? 0)
    const h2 = Number(v.endtime?.slice(0, 2) ?? 0)
    const m2 = Number(v.endtime?.slice(2, 4) ?? 0)

    const start = setTime(base, { hours: h1, minutes: m1, seconds: 0, milliseconds: 0 })
    const end = setTime(base, { hours: h2, minutes: m2, seconds: 0, milliseconds: 0 })

    return { start, end }
  } catch {
    return { start: undefined as Date | undefined, end: undefined as Date | undefined }
  }
}

const Visits = ({ intervention }: Props) => {
  const { t } = useLingui()
  const { auth } = useAuth()
  const technicians = useBoundStore((s) => s.technicians)
  const [openItems, setOpenItems] = useState<string[]>([]) 

  const dbid = auth.data?.databases?.[0]?.dbid
  const klantid = intervention?.m_iKLANTID

  const futureQueryKey = useMemo(
    () => [
      'intervention-future',
      { dbid, interventionid: intervention?.m_iINTERVENTIESID, callid: intervention?.m_iCALLID },
    ],
    [dbid, intervention?.m_iINTERVENTIESID, intervention?.m_iCALLID]
  )

  const {
    data: futureResp,
    isLoading: futureLoading,
    isError: futureError,
  } = useQuery<FutureResponse>({
    queryKey: futureQueryKey,
    enabled: !!dbid && !!intervention?.m_iINTERVENTIESID && !!intervention?.m_iCALLID,
    queryFn: async () => {
      const resp = await axios.get<FutureResponse>(
        `intervention/future?dbid=${dbid}&interventionid=${intervention?.m_iINTERVENTIESID}&callid=${intervention?.m_iCALLID}`
      )
      return resp.data
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })

  const futureItems: FutureVisit[] = futureResp?.interventions ?? []
  const futureCount = futureItems.length

  const historyQueryKey = useMemo(
    () => ['intervention-history-inline', { dbid, klantid }],
    [dbid, klantid]
  )

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<InterventionHistoryResponse, Error>({
    queryKey: historyQueryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const resp = await axios.get<InterventionHistoryResponse>(
        `intervention/history?dbid=${dbid}&klantid=${klantid}&page=${pageParam}`
      )
      return resp.data
    },
    enabled: !!dbid && !!klantid,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalpages ? lastPage.page + 1 : undefined,
    placeholderData: (prev) => prev,
    gcTime: 5 * 60 * 1000,
  })

  const items: Intervention[] =
    data?.pages?.flatMap((p) => p.interventions ?? []) ?? []
  const totalRecords = data?.pages?.[0]?.totalrecords ?? 0

  return (
    <Accordion
      type="multiple"
      value={openItems}
      onValueChange={(v) => setOpenItems(v as string[])}
      className="flex flex-col gap-2"
    >
      {/* Andere bezoeken */}
      <AccordionItem value='future-visits' className='border rounded-md'>
        <AccordionTrigger className='p-2 hover:no-underline'>
          <div className='flex flex-col text-left'>
            <p className='text-sm font-semibold'><Trans>Andere bezoeken</Trans></p>

            {futureLoading && (
              <p className='text-sm text-neutral-400'><Trans>Bezig met laden...</Trans></p>
            )}

            {!futureLoading && futureError && (
              <p className='text-sm text-red-600'><Trans>Kon de andere bezoeken niet laden.</Trans></p>
            )}

            {!futureLoading && !futureError && futureCount === 0 && (
              <p className='text-sm text-neutral-400'><Trans>Geen andere bezoeken gevonden</Trans></p>
            )}

            {!futureLoading && !futureError && futureCount > 0 && (
              <p className='text-sm text-neutral-400'>
                <Trans>{futureCount} andere bezoeken gevonden</Trans>
              </p>
            )}
          </div>
        </AccordionTrigger>

        <AccordionContent className='p-2 space-y-4'>
          {!futureLoading && !futureError && futureItems.map((v, idx) => {
            const { start, end } = toDateSpan(v)
            const dateLine = start
              ? `${format(start, 'dd/MM/yyyy HH:mm')} - ${end ? format(end, 'HH:mm') : ''}`
              : ''

            return (
              <div key={`future-${idx}`} className='py-2 border-b last:border-b-0'>
                <p className='font-bold'>{v.name || 'Onbekende naam'}</p>
                <p className='text-neutral-500'>{dateLine || 'Onbekende datum/uren'} </p>
              </div>
            )
          })}
        </AccordionContent>
      </AccordionItem>

      {/* Vorige bezoeken */}
      <AccordionItem value='previous-visits' className='border rounded-md'>
        <AccordionTrigger className='p-2 hover:no-underline'>
          <div className='flex flex-col text-left'>
            <p className='text-sm font-semibold'><Trans>Vorige bezoeken</Trans></p>

            {isLoading && (
              <p className='text-sm text-neutral-400'><Trans>Bezig met laden...</Trans></p>
            )}

            {!isLoading && totalRecords === 0 && (
              <p className='text-sm text-neutral-400'><Trans>Er zijn geen andere bezoeken gevonden</Trans></p>
            )}

            {!isLoading && totalRecords > 0 && (
              <p className='text-sm text-neutral-400'>
                <Trans>{totalRecords} andere bezoeken gevonden</Trans>
              </p>
            )}
          </div>
        </AccordionTrigger>

        <AccordionContent className='p-2 space-y-6'>
          {isError && (
            <p className='text-sm text-red-600'><Trans>Kon de bezoeken niet laden.</Trans></p>
          )}

          {items.map((history) => {
            const technician = technicians?.find(
              (t: any) => t?.m_iTECHNIEKERID?.toString() === history?.m_iTECHNIEKERID?.toString()
            )

            return (
              <div key={`history-${history.m_iINTERVENTIESID}`}>
                <div className='flex items-center justify-between py-2 border-b'>
                  <p className='font-bold'>{technician?.m_sNAAM}</p>
                  <p className='text-neutral-400'>{history?.m_dDATUM || t`Geen datum`}</p>
                </div>

                <div className='py-2'>
                  <p className='font-medium text-neutral-500'><Trans>Reden</Trans></p>
                  <p className='font-medium text-neutral-800'>{history?.m_sOMSCHRIJVING || t`Geen reden`}</p>
                </div>

                <div className='py-2'>
                  <p className='font-medium text-neutral-500'><Trans>Geboden oplossing</Trans></p>
                  {(history?.m_sOPLOSSING || history?.m_sOPLOSSINGEXTRA)
                    ? <p className='font-medium text-neutral-800'>{history?.m_sOPLOSSING} {history?.m_sOPLOSSINGEXTRA}</p>
                    : <p className='font-medium text-neutral-800'><Trans>Geen oplossing</Trans></p>}
                </div>

                <div className='py-2'>
                  <p className='font-medium text-neutral-500'><Trans>Interne commentaar</Trans></p>
                  <p className='font-medium text-neutral-800'>{history?.m_sINTCOMM || t`Geen interne commentaar`}</p>
                </div>

                {getCustomSettings(auth.data?.databases?.[0]?.dbid)?.intervention?.start_page?.enable_visit_rapport && (
                  <Link to={`/startpagina/bezoekrapport?interventionid=${history?.m_iINTERVENTIESID}`}>
                    <Button variant='secondary' className='w-full'>
                      <Trans>Bezoekrapport bekijken</Trans>
                    </Button>
                  </Link>
                )}
              </div>
            )
          })}

          {totalRecords > 0 && (
            <div className='flex items-center justify-center'>
              <Button
                variant='outline'
                disabled={!hasNextPage || isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage
                  ? <Trans>Bezig met laden…</Trans>
                  : hasNextPage
                    ? <Trans>Meer laden</Trans>
                    : <Trans>Alles geladen</Trans>}
              </Button>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export default Visits
